import {
  Airline,
  OnlyAviaPackage,
  ProviderDiagnostic,
  ProviderDiagnosticStatus,
  SearchDiagnostics,
  SearchOnlyAviaPackagesResponse
} from '@appTypes/api.types';

const code = (value: string | null | undefined) => (value ?? '').trim().toUpperCase();

/** The flight providers that sent offers in an answer (the offers' supplier codes, shown where the site shows supplier tags). */
const offeringProviders = (packages: ReadonlyArray<OnlyAviaPackage> | null | undefined): Set<string> =>
  new Set((packages ?? []).map((p) => code(p.flight?.supplierCode)).filter((c) => !!c));

/**
 * docs features/website/flight-search-progressive.md R-PF6: "Some suppliers did not answer" names only a provider that did not answer at all - a provider
 * whose offers are on the list answered, even when one of its routes or classes timed out.
 *
 * Kazunion Website Flags (website-flags.md R-WF17, plan B-4): online-api applies R-PF6 to every answer before it hides the supplier tags, so one
 * class search's list arrives already clean for every agent and is used as it is. Only the join of several class searches is left here (another
 * class's offers can answer for a provider): online-api never sees the classes together. It reads the offers' supplier codes, so it can do that
 * only for an agent who sees the tags - for the others each class's own list (already clean) is joined.
 */
const notAnswered = (
  named: ReadonlyArray<string>,
  packages: ReadonlyArray<OnlyAviaPackage> | null | undefined
): string[] => {
  const offering = offeringProviders(packages);
  return named.filter((c) => !offering.has(code(c)));
};

const statusOf = (row: ProviderDiagnostic): ProviderDiagnosticStatus => {
  if (row.status) return row.status;
  if (row.outcome === 'ok') return 'answered';
  return row.outcome;
};

/**
 * R-PF4: one row per provider over the class searches - pending while any class still asks it; answered when it sent offers in any class (partial when
 * another class or route failed); failed / timeout only when it sent nothing and a call failed. Offers are added up, the time is the longest.
 */
export const mergeAviaClassDiagnostics = (
  answers: ReadonlyArray<SearchOnlyAviaPackagesResponse | null>
): SearchDiagnostics | null => {
  const withRows = answers.filter((a): a is SearchOnlyAviaPackagesResponse => !!a?.diagnostics?.providers?.length);
  if (!withRows.length) return null;
  if (withRows.length === 1 && answers.length === 1) return withRows[0].diagnostics ?? null;

  const byProvider = new Map<string, { rows: ProviderDiagnostic[]; offered: number }>();
  for (const answer of withRows) {
    const offering = new Map<string, number>();
    for (const p of answer.packages ?? []) {
      const c = code(p.flight?.supplierCode);
      if (c) offering.set(c, (offering.get(c) ?? 0) + 1);
    }
    for (const row of answer.diagnostics!.providers!) {
      const key = code(row.provider);
      const entry = byProvider.get(key) ?? { rows: [], offered: 0 };
      entry.rows.push(row);
      entry.offered += offering.get(key) ?? 0;
      byProvider.set(key, entry);
    }
  }

  const providers: ProviderDiagnostic[] = [...byProvider.values()].map(({ rows, offered }) => {
    const statuses = rows.map(statusOf);
    const counts = rows.map((r) => r.count ?? r.results).filter((n): n is number => typeof n === 'number');
    const count = Math.max(
      offered,
      counts.reduce((a, b) => a + b, 0)
    );
    const failed = statuses.some((s) => s === 'failed' || s === 'timeout' || s === 'partial');
    let status: ProviderDiagnosticStatus;
    if (statuses.includes('pending')) status = 'pending';
    else if (count > 0) status = failed ? 'partial' : 'answered';
    else if (failed) status = statuses.find((s) => s === 'timeout' || s === 'failed') ?? 'failed';
    else status = 'empty';
    const durations = rows.map((r) => r.durationMs ?? r.ms ?? 0);
    const started = rows
      .map((r) => r.startedAt)
      .filter((d): d is string => !!d)
      .sort((a, b) => a.localeCompare(b));
    const answeredAt = rows
      .map((r) => r.answeredAt)
      .filter((d): d is string => !!d)
      .sort((a, b) => a.localeCompare(b));
    const ms = Math.max(...durations);
    const unanswered = status === 'failed' || status === 'timeout' || status === 'pending';
    return {
      ...rows[0],
      status,
      outcome: status === 'answered' ? 'ok' : status,
      ms,
      durationMs: ms,
      count: unanswered ? null : count,
      results: unanswered ? null : count,
      startedAt: started[0] ?? rows[0].startedAt ?? null,
      answeredAt: status === 'pending' ? null : (answeredAt[answeredAt.length - 1] ?? rows[0].answeredAt ?? null),
      error: rows.find((r) => r.error)?.error ?? null,
      errorCode: unanswered ? (rows.find((r) => r.errorCode)?.errorCode ?? null) : null
    };
  });

  const complete = answers.every((a) => a?.searchComplete !== false);
  return {
    ...withRows[0].diagnostics,
    totalMs: Math.max(...withRows.map((a) => a.diagnostics?.totalMs ?? 0)),
    complete,
    providers
  };
};

/**
 * Several classes in one search, website only (docs features/website/avia-class-multi-select R-CM4 / R-CM6): the website runs one
 * SearchOnlyAviaPackages per ticked class and joins the answers into the one list the page already shows. This works because the Avia
 * results come back in one page (the website sends no pageRowCount and shows no pager) and every package id is self-contained
 * (`onlyavia:{SearchId}:{FlightId}` in packages-api), so a package from any of the searches opens, re-prices and goes to the cart as before.
 *
 * `answers` holds one entry per class search: the response, or null when that search failed (or has not answered yet, while progressive). Null comes back
 * only when every search failed. Packages are sorted by total price (cheapest first, a package without a price last); airlines are unique by code, in
 * first-seen order. The flight providers that did not answer (flights-api#3, R-FU4) are joined too, each once - minus a provider whose offers are on the
 * list (flight-search-progressive R-PF6; one search's list is already clean from online-api, R-WF17). Progressive answers (R-PF2) are complete when every class search is; the progress counts one row per provider.
 */
export const joinAviaClassSearches = (
  answers: ReadonlyArray<SearchOnlyAviaPackagesResponse | null>
): SearchOnlyAviaPackagesResponse | null => {
  const answered = answers.filter((a): a is SearchOnlyAviaPackagesResponse => a !== null);
  if (!answered.length) return null;
  // one class search: its answer as online-api sent it (R-PF6 already applied there, R-WF17)
  if (answered.length === 1 && (answers.length === 1 || !answered[0].progressId)) return answered[0];

  const price = (p: { totalPrice?: number } | null | undefined) => p?.totalPrice ?? Number.POSITIVE_INFINITY;
  const packages = answered.flatMap((a) => a.packages ?? []).sort((a, b) => price(a) - price(b));

  const seen = new Set<string>();
  const airlines: Airline[] = [];
  for (const airline of answered.flatMap((a) => a.airlines ?? [])) {
    const key = airline.airlineCode ?? airline.airlineName ?? '';
    if (seen.has(key)) continue;
    seen.add(key);
    airlines.push(airline);
  }

  const providersUnavailable = notAnswered(
    [...new Map(answered.flatMap((a) => a.providersUnavailable ?? []).map((c) => [c.toUpperCase(), c])).values()],
    packages
  );

  const joined: SearchOnlyAviaPackagesResponse = {
    ...answered[0],
    airlines,
    packages,
    providersUnavailable,
    totalCount: answered.reduce((sum, a) => sum + (a.totalCount ?? a.packages?.length ?? 0), 0)
  };

  const progressive = answered.some((a) => a.progressId);
  if (!progressive)
    return { ...joined, diagnostics: mergeAviaClassDiagnostics(answers) ?? answered[0].diagnostics ?? null };

  // a class search that has not answered yet keeps the whole search running
  const complete = answers.length === answered.length && answered.every((a) => a.searchComplete !== false);
  const diagnostics = mergeAviaClassDiagnostics(answers);
  // a class search that has not answered yet still asks every provider
  const answeredProviders = (d: SearchDiagnostics) =>
    answers.length > answered.length ? 0 : d.providers!.filter((p) => p.status !== 'pending').length;
  const pending = [...new Set(answered.flatMap((a) => a.providersPending ?? []).map(code))];
  return {
    ...joined,
    searchComplete: complete,
    pollAfterMs: complete ? null : Math.min(...answered.map((a) => a.pollAfterMs ?? 1000)),
    providersTotal: diagnostics
      ? diagnostics.providers!.length
      : answered.reduce((s, a) => s + (a.providersTotal ?? 0), 0),
    providersAnswered: diagnostics
      ? answeredProviders(diagnostics)
      : answered.reduce((s, a) => s + (a.providersAnswered ?? 0), 0),
    providersPending: pending,
    diagnostics: diagnostics ?? answered[0].diagnostics ?? null
  };
};
